import { UserQueries } from '@/shared/queries'
import { useQuery } from '@tanstack/vue-query'
import { AccessArea, AccessRight } from '@/shared/constants'
import { type MaybeRefOrGetter, toValue } from 'vue'

export function useAuthenticatedUser(enabled: MaybeRefOrGetter<boolean> = true) {
  const query = useQuery({
    ...UserQueries.fetch('me'),
    enabled: () => toValue(enabled)
  })

  return {
    ...query,
    hasPermission(area: AccessArea, ...rights: AccessRight[]) {
      const user = query.data.value

      if (user === undefined) {
        return false
      }

      // Converts permissions from array to object of { [AccessArea]: AccessRight[] } form.
      const permissionsMap = user.permissions.reduce(
        (accumulator, current) => {
          const [area, right] = current.split(':')

          accumulator[area] = accumulator[area] || []
          accumulator[area].push(right)

          return accumulator
        },
        {} as Record<string, string[]>
      )

      // System admin always has permission.
      if (AccessArea.SystemAdministrator in permissionsMap) {
        return true
      }

      // Just check if we have any permissions for the area if no rights are specified.
      if (rights.length < 1) {
        return area in permissionsMap
      }

      // Check exact access rights.
      for (const right of rights) {
        if (permissionsMap[area]?.includes(right)) {
          return true
        }
      }

      // Default to not having permission.
      return false
    }
  }
}
